import React, { useState, useEffect } from "react";
import { API_BASE_URL } from "../config";
import { Check, Store, AlertCircle, Clock, User, ExternalLink } from "lucide-react";
import axios from "axios";

const ShopVerification = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [processingId, setProcessingId] = useState(null);

    useEffect(() => {
        const role = localStorage.getItem("role");
        if (role !== "ADMIN") {
            window.location.href = "/home"; // Redirect non-admins
            return;
        }
        fetchRequests();
    }, []);

    const fetchRequests = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.get(`${API_BASE_URL}/api/admin/pending-sellers`, {
                headers: { Authorization: token }
            });

            // แปลงข้อมูลจาก API ให้ตรงกับ UI
            const pendingData = response.data.map(item => ({
                id: item.userId,
                shopName: item.firstName && item.lastName
                    ? `${item.firstName} ${item.lastName}`
                    : item.accountName,
                ownerName: item.firstName && item.lastName
                    ? `${item.firstName} ${item.lastName}`
                    : item.accountName,
                email: item.email,
                phone: item.phoneNumber || "ไม่ระบุ",
                idCardNumber: item.idCardNumber || "ไม่ระบุ",
                currentAddress: item.currentAddress || "ไม่ระบุ",
                registeredAddress: item.registeredAddress || "ไม่ระบุ",
                bankName: item.bankName || "ไม่ระบุ",
                bankAccountName: item.bankAccountName || "ไม่ระบุ",
                bankAccountNumber: item.bankAccountNumber || "ไม่ระบุ",
                idCardImage: item.idCardPhotoPath,
                selfieImage: item.selfiePhotoPath,
                status: "PENDING",
                submittedAt: item.createdAt
            }));

            setRequests(pendingData);
            setLoading(false);
        } catch (err) {
            console.error("Error fetching requests:", err);
            setError("ไม่สามารถดึงข้อมูลคำขอได้");
            setLoading(false);
        }
    };

    const handleAction = async (requestId, action) => {
        const confirmMessage = action === 'approve'
            ? "คุณแน่ใจหรือไม่ที่จะ 'อนุมัติ' ให้ผู้ใช้งานนี้เป็นผู้ขาย (Verified Seller)?"
            : "คุณแน่ใจหรือไม่ที่จะ 'ปฏิเสธ' คำขอนี้?";

        if (!window.confirm(confirmMessage)) return;

        setProcessingId(requestId);
        try {
            const token = localStorage.getItem("token");

            // เรียก API ตาม action
            const endpoint = action === 'approve'
                ? `${API_BASE_URL}/api/admin/approve-seller/${requestId}`
                : `${API_BASE_URL}/api/admin/reject-seller/${requestId}`;

            await axios.put(endpoint, {}, {
                headers: { Authorization: token }
            });

            // อัปเดต UI
            setRequests(requests.filter(req => req.id !== requestId));
            setSelectedRequest(null);
            setProcessingId(null);
            alert(`ดำเนินการ ${action === 'approve' ? 'อนุมัติ' : 'ปฏิเสธ'} สำเร็จ`);

        } catch (err) {
            console.error("Error handling action:", err);
            console.error("Error response:", err.response?.data);
            console.error("Error status:", err.response?.status);
            alert(`เกิดข้อผิดพลาดในการดำเนินการ: ${err.response?.data?.error || err.message}`);
            setProcessingId(null);
        }
    };

    if (loading) return (
        <div className="min-h-screen bg-gray-100 flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-100 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
                <div className="flex items-center gap-4 mb-8">
                    <div className="p-3 bg-red-600 rounded-lg shadow-lg">
                        <Store className="w-8 h-8 text-white" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">ตรวจสอบคำขอเปิดร้าน</h1>
                        <p className="text-gray-500">จัดการคำขอเปิดร้านค้าใหม่จากผู้ใช้งาน</p>
                    </div>
                </div>

                {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                        <AlertCircle size={20} />
                        {error}
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* List Section */}
                    <div className="lg:col-span-1 space-y-4">
                        {requests.length === 0 ? (
                            <div className="bg-white rounded-xl p-8 text-center text-gray-500 shadow-sm">
                                <Check className="w-12 h-12 mx-auto mb-3 text-green-500" />
                                <p>ไม่มีคำขอที่รอดำเนินการ</p>
                            </div>
                        ) : (
                            requests.map(req => (
                                <div
                                    key={req.id}
                                    onClick={() => setSelectedRequest(req)}
                                    className={`bg-white p-4 rounded-xl cursor-pointer transition-all border-2 ${selectedRequest?.id === req.id
                                        ? "border-red-600 shadow-md"
                                        : "border-transparent hover:border-gray-200 shadow-sm"
                                        }`}
                                >
                                    <div className="flex justify-between items-start mb-2">
                                        <h3 className="font-semibold text-gray-900">{req.shopName}</h3>
                                        <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full flex items-center gap-1">
                                            <Clock size={10} /> รอดำเนินการ
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                                        <User size={14} /> {req.ownerName}
                                    </div>
                                    <div className="text-xs text-gray-400 mt-2">
                                        ส่งเมื่อ: {new Date(req.submittedAt).toLocaleDateString('th-TH')}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Detail Section */}
                    <div className="lg:col-span-2">
                        {selectedRequest ? (
                            <div className="bg-white rounded-2xl shadow-sm p-8">
                                <div className="flex justify-between items-start mb-6">
                                    <div>
                                        <h2 className="text-2xl font-bold text-gray-900 mb-1">{selectedRequest.shopName}</h2>
                                        <p className="text-gray-500">Shop ID: #{selectedRequest.id}</p>
                                    </div>
                                    <div className="flex gap-3">
                                        <button
                                            onClick={() => handleAction(selectedRequest.id, 'reject')}
                                            disabled={processingId === selectedRequest.id}
                                            className="px-4 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                                        >
                                            ปฏิเสธ
                                        </button>
                                        <button
                                            onClick={() => handleAction(selectedRequest.id, 'approve')}
                                            disabled={processingId === selectedRequest.id}
                                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                                        >
                                            {processingId === selectedRequest.id ? (
                                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                            ) : (
                                                <Check size={18} />
                                            )}
                                            อนุมัติร้านค้า
                                        </button>
                                    </div>
                                </div>

                                <div className="grid md:grid-cols-2 gap-8">
                                    {/* ข้อมูลส่วนตัว */}
                                    <div className="space-y-4">
                                        <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">ข้อมูลผู้สมัคร</h3>
                                        <div className="space-y-3 text-sm">
                                            <div className="flex justify-between py-2 border-b border-gray-100">
                                                <span className="text-gray-500">ชื่อ-นามสกุล</span>
                                                <span className="font-medium text-gray-900">{selectedRequest.ownerName}</span>
                                            </div>
                                            <div className="flex justify-between py-2 border-b border-gray-100">
                                                <span className="text-gray-500">อีเมล</span>
                                                <span className="font-medium text-gray-900">{selectedRequest.email}</span>
                                            </div>
                                            <div className="flex justify-between py-2 border-b border-gray-100">
                                                <span className="text-gray-500">เบอร์โทรศัพท์</span>
                                                <span className="font-medium text-gray-900">{selectedRequest.phone}</span>
                                            </div>
                                            <div className="flex justify-between py-2 border-b border-gray-100">
                                                <span className="text-gray-500">เลขบัตรประชาชน</span>
                                                <span className="font-medium text-gray-900">{selectedRequest.idCardNumber}</span>
                                            </div>
                                            <div className="py-2 border-b border-gray-100">
                                                <span className="text-gray-500 block mb-1">ที่อยู่ปัจจุบัน</span>
                                                <span className="font-medium text-gray-900 block">{selectedRequest.currentAddress}</span>
                                            </div>
                                            <div className="py-2 border-b border-gray-100">
                                                <span className="text-gray-500 block mb-1">ที่อยู่ตามบัตรประชาชน</span>
                                                <span className="font-medium text-gray-900 block">{selectedRequest.registeredAddress}</span>
                                            </div>
                                        </div>

                                        <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mt-6 mb-3">ข้อมูลธนาคาร</h3>
                                        <div className="space-y-3 text-sm">
                                            <div className="flex justify-between py-2 border-b border-gray-100">
                                                <span className="text-gray-500">ธนาคาร</span>
                                                <span className="font-medium text-gray-900">{selectedRequest.bankName}</span>
                                            </div>
                                            <div className="flex justify-between py-2 border-b border-gray-100">
                                                <span className="text-gray-500">ชื่อบัญชี</span>
                                                <span className="font-medium text-gray-900">{selectedRequest.bankAccountName}</span>
                                            </div>
                                            <div className="flex justify-between py-2 border-b border-gray-100">
                                                <span className="text-gray-500">เลขบัญชี</span>
                                                <span className="font-medium text-gray-900">{selectedRequest.bankAccountNumber}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* เอกสารแนบ */}
                                    <div>
                                        <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">เอกสารแนบ</h3>
                                        <div className="space-y-3">
                                            {/* รูปบัตรประชาชน */}
                                            <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg">
                                                <span className="text-gray-700">รูปบัตรประชาชน</span>
                                                {selectedRequest.idCardImage ? (
                                                    <a
                                                        href={selectedRequest.idCardImage.startsWith('/images/')
                                                            ? `${API_BASE_URL}${selectedRequest.idCardImage}`
                                                            : `${API_BASE_URL}/images/seller-verified/${selectedRequest.idCardImage.split(/[\\/]/).pop()}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="flex items-center gap-1 text-red-600 hover:text-red-700 font-medium"
                                                    >
                                                        <ExternalLink size={16} />
                                                        เปิดดูรูป
                                                    </a>
                                                ) : (
                                                    <span className="text-gray-400">ไม่มีรูป</span>
                                                )}
                                            </div>
                                            {/* รูปเซลฟี่คู่บัตร */}
                                            <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg">
                                                <span className="text-gray-700">รูปถ่ายคู่บัตรประชาชน</span>
                                                {selectedRequest.selfieImage ? (
                                                    <a
                                                        href={selectedRequest.selfieImage.startsWith('/images/')
                                                            ? `${API_BASE_URL}${selectedRequest.selfieImage}`
                                                            : `${API_BASE_URL}/images/seller-verified/${selectedRequest.selfieImage.split(/[\\/]/).pop()}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="flex items-center gap-1 text-red-600 hover:text-red-700 font-medium"
                                                    >
                                                        <ExternalLink size={16} />
                                                        เปิดดูรูป
                                                    </a>
                                                ) : (
                                                    <span className="text-gray-400">ไม่มีรูป</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-white rounded-2xl shadow-sm p-12 text-center h-full flex flex-col items-center justify-center text-gray-500">
                                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                    <Store size={32} className="text-gray-400" />
                                </div>
                                <h3 className="text-lg font-medium text-gray-900">เลือกรายการเพื่อดูรายละเอียด</h3>
                                <p>คลิกที่รายการทางด้านซ้ายเพื่อตรวจสอบข้อมูล</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ShopVerification;
